import { useState, useEffect } from "react";
import DateSelector from "../../components/shared/fields/DateSelector";
import GridCard from "../../components/shared/gridCard/GridCard";
import Datatable from "../../components/shared/datatable/Datatable";
import Button from "../../components/shared/buttons/button";
import { PlusSvg } from "../../svgFiles/PlusSvg";
import MixingModal from "./MixingModal";
import useTranslator from "../../hooks/useTranslator";
import { formatDateAndTime, getCurrentDate, makePriorLotNumber } from "../../helpers/Utils";
import { PROJECT_DATE_FORMAT } from "../../helpers/constants";
import KgGreen from "../../images/kg_green.svg";
import { useGetMixingLogsQuery, useGetMixingSummaryQuery } from "./mixingApi";

const Mixing = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));
  const [currentLotNumber] = useState(makePriorLotNumber(currentDate));
  const [page, setPage] = useState(1);

  useEffect(() => { setLotNumber(makePriorLotNumber(date)); }, [date]);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [addRecord, setAddRecord] = useState(false);
  const [filter, setFilter] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({
    batch_code: "",
    date: "",
    status: "",
  });

  const handleFilterApply = (newFilters) => {
    setAppliedFilters(newFilters);
    setFilter(newFilters);
  };

  const mixingLogLimit = 10;

  const { data: logsData, isLoading, isFetching } = useGetMixingLogsQuery({ 
    page,
    per_page: mixingLogLimit,
    ...filter,
  }, { refetchOnMountOrArgChange: true });



  const { data: summaryData } = useGetMixingSummaryQuery({ date , "batch_code" : lotNumber });

  const handleAdd = () => { setAddRecord(true); setModalMode("add"); setSelectedRecord(null); setShowModal(true); };  const handleEdit = (row) => { setAddRecord(true); setModalMode("edit"); setSelectedRecord(row); setShowModal(true); };

  const listItems = [
    { label: _t("total_entered_weight", "Total Entered Weight"), value: summaryData?.data?.total_entered_weight ?? 0, icon: KgGreen },
    { label: _t("total_cheeks_entered", "Total Cheeks Entered"), value: summaryData?.data?.total_cheeks_entered ?? 0, icon: KgGreen },
    { label: _t("total_strips_entered", "Total Strips Entered"), value: summaryData?.data?.total_strips_entered ?? 0, icon: KgGreen },
    { label: _t("total_entered_from_brokerage", "Total KG From Brokerage"), value: summaryData?.data?.total_entered_from_brokerage ?? 0, icon: KgGreen },
  ];

  const columns = [
    { label: _t("date", "Date"), accessor: "created_at", cell: ({ value }) => value ? formatDateAndTime(value, PROJECT_DATE_FORMAT) : "" },
    { label: _t("batch", "Batch"), accessor: "batch_code" , cell: ({value}) => value ? `#${value}` : "" },
    { label: _t("kg_entered", "KG Entered"), accessor: "kg_entered" },
    { label: _t("water_weight", "Water Weight"), accessor: "water_weight" },
    { label: _t("weight_after_mix", "Weight After Mix"), accessor: "total_weight" },
    {
      label: _t("status", "Status"), accessor: "status",
      cell: ({ value }) => (
        <span className={value === "packed" ? "cmn_badge" : value === "mixing" ? "cmn_badge inactive" : "cmn_badge rejected"}>
          {value === "pre_mix" ? _t("status_pre_mix", "Pre Mix") : value === "mixing" ? _t("status_mixing", "Mixing") : value === "packed" ? _t("status_packed", "Packed") : value}
        </span>
      ),
    },
    {
      label: _t("action", "Action"), accessor: "id",
      cell: ({ row }) => (
        <Button size="small" className={row.status !== "pre_mix" ? "borderRedBtn" : "" } label={row.status === "pre_mix" ? _t("edit", "Edit") : _t("view", "View")} onClick={() => handleEdit(row)} />
      ),
    },
  ];

  return (
    <div>
      {addRecord && (
        <MixingModal show={showModal} setShow={setAddRecord} record={selectedRecord} mode={modalMode} lotNumber={currentLotNumber} />
      )}

      <div className="overview cmn_card mb-3">
        <div className="overview_header d-flex gap-2 align-items-center">
          <h6 className="mb-0 flex-grow-1">{_t("mixing_overview", "Mixing Overview")}</h6>
          <div className="d-flex gap-2 align-items-center">
            <h6 className="mb-0">{_t("batch_code", "Batch Code")}</h6>
            <Button label={`#${lotNumber ?? 0}`} size="small" className="borderRedBtn" />
            <DateSelector setDate={setDate} />
          </div>
        </div>
        <ul className="m-0 grid_card_wrapper">
          {listItems.map((list, index) => (
            <GridCard key={index} title={list.label} value={list.value} icon={list.icon}
              day={currentDate === date ? _t("common_today", "Today") : formatDateAndTime(date, PROJECT_DATE_FORMAT)} />
          ))}
        </ul>
      </div>

      <Datatable
        title={_t("mixing_logs", "Mixing Logs")}
        columns={columns}
        tableData={logsData?.data || ""}
        isLoading={isLoading || isFetching}
        buttons={[{ label: _t("add_mixing_log", "Add Mixing Log"), onClick: handleAdd, icon: PlusSvg }]}
        showPegination={true}
        page={page}
        onPageChange={setPage}
        showCount={true}
        pageLimit={mixingLogLimit}
        showFilterButton={true}
        showFilter={handleFilterApply}
        filterModule="mixing_filter"
        filters={appliedFilters}
      />
    </div>
  );
};

export default Mixing;
